import React, { useEffect, useState } from 'react';
import {
Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Paper,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  LinearProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {

  VisibilityOff as InactiveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  Language as WebIcon,
  Star as StarIcon,
  Update as UpdateIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  superAdminFetchAllPlans,  // Use admin API
  deletePlan,
  renewPlan,
  clearSuccess,
  clearError,
} from '../../redux/slices/planSlice';
import PlanForm from './PlanForm';
import PriceCalculator from './PriceCalculator';

const PlanList = () => {
  const dispatch = useDispatch();
  // Use adminPlans from Redux state
  const { adminPlans, loading, error, success } = useSelector((state) => state.plans);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRenewDialog, setOpenRenewDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tabValue, setTabValue] = useState(0);
  const [renewDays, setRenewDays] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Fetch all plans for super admin (including inactive)
    dispatch(superAdminFetchAllPlans({ 
      includeExpired: true, 
      includeInactive: true 
    }));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      setSnackbar({
        open: true,
        message: 'Operation completed successfully!',
        severity: 'success',
      });
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error',
      });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setOpenDialog(true);
  };

  const handleDelete = (plan) => {
    setSelectedPlan(plan);
    setOpenDeleteDialog(true);
  };

  const handleRenew = (plan) => {
    setSelectedPlan(plan);
    setRenewDays(plan.planValidity || 30);
    setOpenRenewDialog(true);
  };

  const confirmDelete = async () => {
    await dispatch(deletePlan(selectedPlan._id));
    setOpenDeleteDialog(false);
    setSelectedPlan(null);
    // Refresh the list
    dispatch(superAdminFetchAllPlans({ includeExpired: true, includeInactive: true }));
  };

  const confirmRenew = async () => {
    await dispatch(renewPlan({ id: selectedPlan._id, validityDays: renewDays }));
    setOpenRenewDialog(false);
    setSelectedPlan(null);
    dispatch(superAdminFetchAllPlans({ includeExpired: true, includeInactive: true }));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
  };

  const handleCalculate = (plan) => {
    setSelectedPlan(plan);
    setOpenCalculator(true);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'WEB':
        return <WebIcon fontSize="small" />;
      case 'ANDROID':
        return <AndroidIcon fontSize="small" />;
      case 'IOS':
        return <AppleIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusChip = (plan) => {
    if (!plan.isActive) {
      return <Chip icon={<InactiveIcon />} label="Inactive" color="default" size="small" />;
    }
    
    const today = new Date();
    const expiryDate = plan.expiryDate ? new Date(plan.expiryDate) : null;
    
    if (expiryDate && expiryDate < today) {
      return <Chip icon={<ErrorIcon />} label="Expired" color="error" size="small" />;
    }
    
    if (expiryDate) {
      const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 7) {
        return <Chip icon={<WarningIcon />} label={`Expires in ${daysUntilExpiry} days`} color="warning" size="small" />;
      }
      return <Chip icon={<CheckCircleIcon />} label={`Valid for ${daysUntilExpiry} days`} color="success" size="small" />;
    }
    
    return <Chip icon={<CheckCircleIcon />} label="Active" color="success" size="small" />;
  };

  // Filter plans based on search and tabs
  const filteredPlans = adminPlans.filter(plan => {
    // Search filter
    const matchesSearch = plan.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Tab filter
    if (tabValue === 0) return matchesSearch; // All plans
    if (tabValue === 1) return matchesSearch && plan.isActive; // Active
    if (tabValue === 2) return matchesSearch && !plan.isActive; // Inactive
    
    // Expired filter
    if (tabValue === 3) {
      const today = new Date();
      const expiryDate = plan.expiryDate ? new Date(plan.expiryDate) : null;
      return matchesSearch && expiryDate && expiryDate < today;
    }
    
    return false;
  });

  if (loading && adminPlans.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Subscription Plans
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="Search plans..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Create New Plan
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label={`All (${adminPlans.length})`} />
          <Tab label={`Active (${adminPlans.filter(p => p.isActive).length})`} />
          <Tab label={`Inactive (${adminPlans.filter(p => !p.isActive).length})`} />
          <Tab label={`Expired (${adminPlans.filter(p => {
            const today = new Date();
            const expiryDate = p.expiryDate ? new Date(p.expiryDate) : null;
            return expiryDate && expiryDate < today;
          }).length})`} />
        </Tabs>
      </Paper>

      {/* Alert for inactive plans */}
      {adminPlans?.filter(p => !p.isActive).length > 0 && tabValue === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {adminPlans.filter(p => !p.isActive).length} inactive 
          plan(s) that are hidden from regular users.
        </Alert>
      )}

      <Grid container spacing={3}>
        {filteredPlans?.map((plan) => (
          <Grid item xs={12} md={6} lg={4} key={plan._id}>
            <Card 
              elevation={plan.isPopular ? 8 : 1}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'transform 0.2s',
                opacity: (!plan.isActive) ? 0.7 : 1,
                border: !plan.isActive ? '2px dashed #ccc' : 'none',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              {plan?.isPopular && plan.isActive && (
                <Chip
                  label="POPULAR"
                  color="primary"
                  size="small"
                  icon={<StarIcon />}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    fontWeight: 'bold',
                  }}
                />
              )}
              
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h5" component="h2" fontWeight="bold">
                    {plan.name}
                    
                    {!plan.isActive && (
                      <Chip 
                        label="Inactive" 
                        size="small" 
                        color="default"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  {getStatusChip(plan)}
                </Box>

                <Box display="flex"  mb={1}>
                  <Typography fontSize={14} color="text.secondary">
                    {plan.title}
                  </Typography>
                </Box>

                <Box display="flex" gap={0.5} mb={2}>
                  {plan.platforms?.map((platform) => (
                    <Chip
                      key={platform}
                      icon={getPlatformIcon(platform)}
                      label={platform}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Box mb={2}>
                  <Box display="flex" alignItems="baseline" gap={2}>
                    <Box>
                      <Typography variant="h6" color="primary">
                        ₹{plan.billingOptions?.monthly?.price}
                        <Typography component="span" variant="body2" color="text.secondary">
                          /month
                        </Typography>
                      </Typography>
                      {plan.billingOptions?.monthly?.discount > 0 && (
                        <Typography variant="caption" color="success.main">
                          {plan.billingOptions.monthly.discount}% off
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="h6" color="secondary">
                        ₹{plan.billingOptions?.yearly?.price}
                        <Typography component="span" variant="body2" color="text.secondary">
                          /year
                        </Typography>
                      </Typography>
                      {plan.billingOptions?.yearly?.discount > 0 && (
                        <Typography variant="caption" color="success.main">
                          {plan.billingOptions.yearly.discount}% off
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {plan.expiryDate && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Expires: {new Date(plan.expiryDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}

                {plan.hasTrial && (
                  <Chip 
                    label={`${plan.trialDays} days trial`}
                    color="info"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}

                 {plan.customPlan && (
                  <Chip 
                    label="Custom Plan"
                    color="secondary"
                    size="small"
                    sx={{ mb: 2, ml:2 }}
                  />
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>{plan.dealerLimit}</strong> Dealers included
                </Typography>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Extra dealer: <strong>${plan.extraDealerPrice}</strong>/month
                </Typography>

                {plan.setupFee > 0 && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Setup fee: <strong>${plan.setupFee}</strong>
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Features:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {plan.features?.slice(0, 4).map((feature, index) => (
                    <Typography component="li" variant="body2" key={index}>
                      {feature}
                    </Typography>
                  ))}
                  {plan.features?.length > 4 && (
                    <Typography variant="body2" color="text.secondary">
                      +{plan.features.length - 4} more
                    </Typography>
                  )}
                </Box>
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                <Box>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEdit(plan)} color="primary" size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleDelete(plan)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                  {plan.expiryDate && new Date(plan.expiryDate) < new Date() && (
                    <Tooltip title="Renew Plan">
                      <IconButton onClick={() => handleRenew(plan)} color="success" size="small">
                        <UpdateIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => handleCalculate(plan)}
                >
                  Calculate Price
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Plan Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPlan ? 'Edit Plan' : 'Create New Plan'}
        </DialogTitle>
        <DialogContent>
          <PlanForm 
            plan={selectedPlan} 
            onClose={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the plan "{selectedPlan?.name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renew Plan Dialog */}
      <Dialog open={openRenewDialog} onClose={() => setOpenRenewDialog(false)}>
        <DialogTitle>Renew Plan - {selectedPlan?.name}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Extend the validity of this plan
          </Typography>
          <TextField
            fullWidth
            label="Validity Days"
            type="number"
            value={renewDays}
            onChange={(e) => setRenewDays(parseInt(e.target.value) || 30)}
            InputProps={{ inputProps: { min: 1, max: 365 } }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRenewDialog(false)}>Cancel</Button>
          <Button onClick={confirmRenew} color="primary" variant="contained">
            Renew Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price Calculator Dialog */}
      <Dialog 
        open={openCalculator} 
        onClose={() => setOpenCalculator(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Calculate Price - {selectedPlan?.name}
        </DialogTitle>
        <DialogContent>
          <PriceCalculator 
            plan={selectedPlan} 
            onClose={() => setOpenCalculator(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PlanList;